import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import { formatDate } from '../utils/dateFormat';

const TYPE_META = {
  note:     { icon: '📝' },
  task:     { icon: '✅' },
  reminder: { icon: '🔔' },
};
const PRIORITIES = ['low', 'normal', 'high'];
const PRIO_DOT = { low: '⚪', normal: '🟡', high: '🔴' };

const todayStr = () => new Date().toISOString().split('T')[0];

const EMPTY = { content: '', entryType: 'note', priority: 'normal', dueDate: '', pinned: false };

export default function NotebookPage() {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');            // '', 'task', 'note', 'reminder'
  const [quick, setQuick] = useState('');
  const [quickType, setQuickType] = useState('note');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState('');
  const [doneOpen, setDoneOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = filter ? `?type=${filter}` : '';
    api.get(`/notebook${params}`)
      .then(r => setEntries(r.data.data || []))
      .catch(() => toast.error(t('notebook.loadFailed')))
      .finally(() => setLoading(false));
  }, [filter, t]);

  useEffect(() => { load(); }, [load]);

  const quickAdd = async () => {
    if (!quick.trim()) return;
    try {
      await api.post('/notebook', { content: quick.trim(), entryType: quickType });
      setQuick('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.error || t('notebook.saveFailed'));
    }
  };

  const submitModal = async (e) => {
    e.preventDefault();
    if (!form.content.trim()) return toast.error(t('notebook.contentRequired'));
    setSaving(true);
    try {
      await api.post('/notebook', {
        content: form.content.trim(), entryType: form.entryType,
        priority: form.priority, dueDate: form.dueDate || null, pinned: form.pinned,
      });
      toast.success(t('notebook.saved'));
      setShowModal(false); setForm(EMPTY); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.error || t('notebook.saveFailed'));
    } finally { setSaving(false); }
  };

  const toggle = async (id) => {
    try { await api.put(`/notebook/${id}/toggle`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || t('notebook.saveFailed')); }
  };
  const pin = async (id) => {
    try { await api.put(`/notebook/${id}/pin`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || t('notebook.saveFailed')); }
  };
  const saveEdit = async (id) => {
    if (!editText.trim()) { setEditId(null); return; }
    try { await api.put(`/notebook/${id}`, { content: editText.trim() }); setEditId(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || t('notebook.saveFailed')); }
  };
  const remove = async (id) => {
    try { await api.delete(`/notebook/${id}`); setConfirmDel(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || t('notebook.saveFailed')); }
  };

  // ─── Bucket entries into display sections ──────────────────────────────
  const today = todayStr();
  const active = entries.filter(e => !e.is_done);
  const done = entries.filter(e => e.is_done);
  const pinned = active.filter(e => e.pinned);
  const rest = active.filter(e => !e.pinned);
  const dueToday = rest.filter(e => e.due_date && e.due_date.split('T')[0] === today);
  const overdue = rest.filter(e => e.due_date && e.due_date.split('T')[0] < today);
  const others = rest.filter(e => !e.due_date || e.due_date.split('T')[0] > today);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const Card = ({ e }) => {
    const meta = TYPE_META[e.entry_type] || TYPE_META.note;
    const isEditing = editId === e.id;
    return (
      <div style={{
        display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 12, marginBottom: 8,
        background: e.pinned ? '#fffdf0' : '#fff',
        border: `1px solid ${e.pinned ? '#f0e6b0' : '#eee'}`,
        opacity: e.is_done ? 0.6 : 1,
      }}>
        {/* Toggle circle */}
        <button onClick={() => toggle(e.id)} title={t('notebook.toggleDone')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', lineHeight: 1.2, padding: 0 }}>
          {e.is_done ? '✅' : '⭕'}
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          {isEditing ? (
            <textarea autoFocus value={editText} rows={2}
              onChange={ev => setEditText(ev.target.value)}
              onBlur={() => saveEdit(e.id)}
              style={{ width: '100%', fontSize: '0.9rem' }} />
          ) : (
            <div onClick={() => { setEditId(e.id); setEditText(e.content); }}
              style={{
                fontSize: '0.92rem', fontWeight: 600, cursor: 'text', whiteSpace: 'pre-wrap',
                textDecoration: e.is_done ? 'line-through' : 'none',
                color: e.is_done ? '#999' : '#1c3a1c',
              }}>
              <span style={{ marginRight: 4 }}>{meta.icon}</span>{e.content}
            </div>
          )}

          {/* Meta line */}
          <div style={{ fontSize: '0.72rem', color: '#7a9472', marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {e.is_done
              ? <span>{t('notebook.completedAt')}: {formatDate(e.done_at)}</span>
              : <>
                  {e.due_date && <span>📅 {formatDate(e.due_date)}</span>}
                  {e.priority === 'high' && <span style={{ color: '#e74c3c', fontWeight: 700 }}>🔴 {t('notebook.prioHigh')}</span>}
                  {e.priority === 'normal' && e.entry_type !== 'note' && <span>🟡 {t('notebook.prioNormal')}</span>}
                  {!e.due_date && !e.is_done && e.entry_type === 'note' && <span>{formatDate(e.created_at)}</span>}
                </>}
          </div>

          {confirmDel === e.id && (
            <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: '0.78rem', color: '#e74c3c' }}>{t('notebook.deleteConfirm')}</span>
              <button onClick={() => remove(e.id)} style={delBtn}>{t('notebook.delete')}</button>
              <button onClick={() => setConfirmDel(null)} style={cancelBtn}>{t('notebook.cancel')}</button>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <button onClick={() => pin(e.id)} title={t('notebook.pinned')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', opacity: e.pinned ? 1 : 0.3, padding: 0 }}>📌</button>
          <button onClick={() => setConfirmDel(e.id)} title={t('notebook.delete')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.95rem', opacity: 0.5, padding: 0 }}>🗑️</button>
        </div>
      </div>
    );
  };

  const Section = ({ title, list }) => list.length === 0 ? null : (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: '0.74rem', fontWeight: 700, color: '#7a9472', margin: '0 4px 6px' }}>{title}</div>
      {list.map(e => <Card key={e.id} e={e} />)}
    </div>
  );

  const TABS = [
    { value: '', label: t('notebook.all') },
    { value: 'task', label: `✅ ${t('notebook.tasks')}` },
    { value: 'note', label: `📝 ${t('notebook.notes')}` },
    { value: 'reminder', label: `🔔 ${t('notebook.reminders')}` },
  ];

  return (
    <div>
      <div className="card-row" style={{ marginBottom: 12 }}>
        <h2 className="section-title" style={{ margin: 0 }}>📓 {t('notebook.title')}</h2>
      </div>

      {/* Quick add bar */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10 }}>
        <input value={quick} onChange={e => setQuick(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') quickAdd(); }}
          placeholder={t('notebook.quickPlaceholder')}
          style={{ flex: 1 }} />
        {Object.entries(TYPE_META).map(([type, m]) => (
          <button key={type} onClick={() => setQuickType(type)}
            title={t(`notebook.type_${type}`)}
            style={{
              background: quickType === type ? 'var(--green-pale)' : '#f4f4f4',
              border: quickType === type ? '1px solid var(--green-mid)' : '1px solid #eee',
              borderRadius: 8, padding: '6px 8px', cursor: 'pointer', fontSize: '1rem',
            }}>{m.icon}</button>
        ))}
        <button onClick={quickAdd}
          style={{ background: 'var(--green-mid)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 11px', cursor: 'pointer', fontSize: '1rem', fontWeight: 700 }}>➕</button>
      </div>

      {/* Type filter tabs */}
      <div className="chip-group" style={{ marginBottom: 12 }}>
        {TABS.map(tb => (
          <button key={tb.value} className={`chip${filter === tb.value ? ' active' : ''}`}
            onClick={() => setFilter(tb.value)}>{tb.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="empty-state"><p>{t('common.loading')}</p></div>
      ) : entries.length === 0 ? (
        <div className="empty-state"><div className="icon">📓</div><p>{t('notebook.empty')}</p></div>
      ) : (
        <>
          <Section title={`📌 ${t('notebook.secPinned')}`} list={pinned} />
          <Section title={`🔔 ${t('notebook.secDueToday')}`} list={dueToday} />
          <Section title={`🔴 ${t('notebook.secOverdue')}`} list={overdue} />
          <Section title={`📋 ${t('notebook.secOthers')}`} list={others} />

          {done.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <button onClick={() => setDoneOpen(o => !o)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, color: '#7a9472', padding: '4px' }}>
                ✅ {t('notebook.secDone')} ({done.length}) {doneOpen ? '▲' : '▼'}
              </button>
              {doneOpen && done.map(e => <Card key={e.id} e={e} />)}
            </div>
          )}
        </>
      )}

      <button className="btn-fab" onClick={() => { setForm(EMPTY); setShowModal(true); }}>+</button>

      {showModal && (
        <Modal title={t('notebook.newEntry')} onClose={() => setShowModal(false)}>
          <form onSubmit={submitModal}>
            <div className="form-group">
              <label>{t('notebook.content')} *</label>
              <textarea value={form.content} onChange={e => setF('content', e.target.value)} rows={3} autoFocus required />
            </div>
            <div className="form-group">
              <label>{t('notebook.type')}</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {Object.keys(TYPE_META).map(type => (
                  <button type="button" key={type} onClick={() => setF('entryType', type)}
                    style={seg(form.entryType === type)}>
                    {TYPE_META[type].icon} {t(`notebook.type_${type}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>{t('notebook.priority')}</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {PRIORITIES.map(p => (
                  <button type="button" key={p} onClick={() => setF('priority', p)}
                    style={seg(form.priority === p)}>
                    {PRIO_DOT[p]} {t(`notebook.prio_${p}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('notebook.dueDate')}</label>
                <input type="date" value={form.dueDate} onChange={e => setF('dueDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label>{t('notebook.pinned')}</label>
                <button type="button" onClick={() => setF('pinned', !form.pinned)} style={seg(form.pinned)}>
                  📌 {form.pinned ? t('common.yes') : t('common.no')}
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setShowModal(false)}>
                {t('notebook.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={saving}>
                {saving ? t('common.loading') : `💾 ${t('notebook.save')}`}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

const seg = (active) => ({
  flex: 1, padding: '8px 6px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700,
  background: active ? 'var(--green-mid)' : '#f4f4f4',
  color: active ? '#fff' : '#555',
  border: active ? '1px solid var(--green-mid)' : '1px solid #eee',
});
const delBtn = { background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' };
const cancelBtn = { background: '#f0f0f0', color: '#555', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' };
